import { COUNTRIES } from "@/i18n/countries";

/**
 * Compact flag row that shows which markets a row is available in.
 *
 * Semantics match the backend `_matches_country` filter:
 *   • Empty / null `codes` → available in EVERY market (renders a
 *     grey "Global" chip so admins can distinguish "unset" from
 *     "restricted-to-one-market").
 *   • Otherwise render one flag per code (up to `max`, then a +N pill).
 *
 * Sits inline in table cells and card headers. No interactivity.
 */
export default function CountryChips({
    codes,
    max = 4,
    size = "sm",
    testid,
}) {
    const list = Array.isArray(codes) ? codes : [];
    const empty = list.length === 0;
    const shown = list.slice(0, max);
    const overflow = list.length - shown.length;
    const flagSize = size === "sm" ? "text-sm" : "text-base";

    if (empty) {
        return (
            <span
                data-testid={testid ? `${testid}-global` : undefined}
                className="inline-flex items-center gap-1 rounded-full bg-navy-50 text-navy-500 text-[10px] uppercase tracking-widest px-2 py-0.5 border border-navy-100"
                title="Available in every market"
            >
                Global
            </span>
        );
    }

    return (
        <span
            data-testid={testid}
            className="inline-flex items-center gap-0.5 rounded-full bg-white border border-navy-100 px-2 py-0.5"
        >
            {shown.map((code) => {
                const meta = COUNTRIES.find((c) => c.code === code);
                return (
                    <span
                        key={code}
                        title={meta?.name || code}
                        className={`${flagSize} leading-none`}
                        data-testid={testid ? `${testid}-${code}` : undefined}
                    >
                        {meta?.flag || code}
                    </span>
                );
            })}
            {overflow > 0 && (
                <span
                    className="ml-1 text-[10px] uppercase tracking-wider text-navy-500"
                    title={list.slice(max).join(", ")}
                >
                    +{overflow}
                </span>
            )}
        </span>
    );
}
